/**
 * 通用工具函数
 */

import { formatUsdt, type FormatUsdtOptions } from './money';

/**
 * 将 Unix 时间戳（秒）转换为本地日期字符串
 * @param timestamp - Unix 时间戳（秒）或 null
 * @param format - 日期格式，默认为 'YYYY-MM-DD'
 * @returns 格式化后的日期字符串，如果输入为 null 则返回 'Never'
 * 
 * @example
 * ```ts
 * formatTimestamp(1717200000) // '2024-06-01'
 * formatTimestamp(null) // 'Never'
 * formatTimestamp(1717200000, 'YYYY-MM-DD HH:mm:ss') // '2024-06-01 12:00:00'
 * ```
 */
export function formatTimestamp(
  timestamp: number | null,
  format: 'date' | 'datetime' = 'date'
): string {
  if (!timestamp) {
    return 'Never';
  }

  const date = new Date(timestamp * 1000); // Unix 时间戳是秒，需要转换为毫秒

  if (format === 'date') {
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * 生成随机字符串
 * @param length - 字符串长度，默认 16
 * @returns 随机字符串
 */
export function generateRandomString(length: number = 16): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Format a USDT balance for display.
 *
 * Thin compatibility shim over {@link formatUsdt} (in `src/utils/money.ts`).
 * Kept around so existing call sites (Dashboard / Layout / RechargeModal …)
 * don't all need to be updated in one go; new code should call `formatUsdt`
 * directly from `@/utils/money` to make the precision-safe path explicit.
 *
 * Rules (delegated to `formatUsdt`):
 *   - Truncate to at most `decimals` fractional digits (no rounding).
 *   - Negative values are truncated toward zero.
 *   - Trailing zeros after the decimal point are stripped; if no fractional
 *     digits remain the dot is removed as well. So 1.1 stays "1.1",
 *     10 becomes "10", 1.2000 becomes "1.2".
 *   - Zero / null / undefined / NaN / Infinity render as the fixed "0.00"
 *     sentinel so empty-balance UI stays visually consistent.
 *   - String inputs (OpenAPI `availableUsdt` 等) are parsed via Decimal so
 *     values exceeding `Number.MAX_SAFE_INTEGER` keep their precision.
 *
 * Examples:
 *   formatUsdtBalance(1.23459)   -> "1.2345"
 *   formatUsdtBalance(1.1)       -> "1.1"
 *   formatUsdtBalance(10)        -> "10"
 *   formatUsdtBalance(-1.23459)  -> "-1.2345"
 *   formatUsdtBalance(0)         -> "0.00"
 *   formatUsdtBalance(undefined) -> "0.00"
 *   formatUsdtBalance("10.123999") -> "10.1239"
 *
 * @param value - 余额数值或 API 字符串；undefined / null / NaN / Infinity 视为 0
 * @param decimals - 最多保留的小数位，默认 4
 * @returns 展示用的余额字符串（不含单位）
 */
export function formatUsdtBalance(
  value: number | string | undefined | null,
  decimals: number = 4,
): string {
  const opts: FormatUsdtOptions = { maxDecimals: decimals };
  return formatUsdt(value, opts);
}

/**
 * Mask the middle of a string, keeping head/tail chars (e.g. App ID display).
 * Returns the original value when it is too short to mask (len <= head + tail).
 *
 * @example
 * ```ts
 * maskMiddle('1234567890123') // '123456***0123'
 * maskMiddle('12345')         // '12345' (too short)
 * maskMiddle(null)            // ''
 * ```
 */
export function maskMiddle(
  value: string | null | undefined,
  head = 6,
  tail = 4,
  mask = '***',
): string {
  if (!value) return '';
  if (value.length <= head + tail) return value;
  return `${value.slice(0, head)}${mask}${value.slice(-tail)}`;
}

/**
 * 复制到剪贴板
 * @param text - 要复制的文本
 * @returns Promise，成功时 resolve，失败时 reject
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    // 降级方案：使用传统的 execCommand 方法
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
